/* eslint-disable @typescript-eslint/no-explicit-any */
import { userLogin } from "@/services/user";
import { jwtDecode } from "jwt-decode";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { setLoginRes } from "../../api";

import { useAnalytics } from "@/analytics/AnalyticsContext";
import { VISITOR_EVENTS } from "@/analytics/events";
import { addUserInformation } from "@/store/userSlice";
import { isEnrolledStatus } from "@/types/employeeState";
import {
  analyzeDemographics,
  getAnonymizedDemographics,
} from "@/utils/demographicAnalysis";
import { identifyVisitor } from "@/utils/visitorIdentification";
import PublicLayout from "../Common/Containers/PublicLayout";
import { LoginForm, LoginFormInput } from "./LoginForm";

export type UserData = {
  readonly UserName: string;
  readonly UserAccountID: string;
  readonly FirstName: string;
  readonly MiddleName: string;
  readonly LastName: string;
  readonly EmailAddress: string;
  readonly EmployeeID: string;
  readonly EmployerID: string;
  readonly SootchyEmployerID: string;
  readonly VestingScheduleName: string;
  readonly IsDefaultPasswordChanged: "True" | "False";
  readonly EnrolledStatus: string;
  readonly DateOfBirth: string;
  readonly EmployeeSalary: string;
};

type UserLoginResponse = {
  readonly accessToken: string;
};

type UserLoginParams = {
  readonly username: string;
  readonly password: string;
};

const Login: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { trackEvent, setUser } = useAnalytics();
  const [isLoading, setIsLoading] = useState(false);
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");

  const search = useLocation().search;
  const searchParams = new URLSearchParams(search).toString();
  /*   const [showNewPasswordScreen, setShowNewPasswordScreen] =
    useState<boolean>(false);
  const user = useSelector((state: RootState) => state.user); */
  const [error, setError] = useState<string | null>(null);

  const params = new URLSearchParams(location.search);
  const employeeData = params.get("employee");

  useEffect(() => {
    if (employeeData) {
      const decodedData = atob(employeeData);
      const jsonData = JSON.parse(decodedData);

      setUsername(jsonData.username?.trim() || "");
      setPassword(jsonData.password?.trim() || "");
    } else {
      setUsername("");
      setPassword("");
    }
  }, [employeeData]);

  const handleLogin = async (loginData: LoginFormInput) => {
    setIsLoading(true);

    try {
      const trimmedUsername = loginData.username.trim();
      const trimmedPassword = loginData.password.trim();

      if (!password || !username) {
        setPassword(trimmedPassword);
        setUsername(trimmedUsername);
      }

      const { data }: { data: UserLoginResponse } = await userLogin({
        username: trimmedUsername,
        password: trimmedPassword,
      } as UserLoginParams);

      if (data?.accessToken) {
        dispatch(addUserInformation(data.accessToken));
        setLoginRes(data);

        const decoded: UserData = jwtDecode(data.accessToken);

        try {
          const userForAnalytics = {
            id: decoded.UserAccountID,
            username: decoded.UserName,
            enrolledStatus: decoded.EnrolledStatus,
            IsDefaultPasswordChanged: decoded.IsDefaultPasswordChanged,
          };

          // Identify visitor type
          const visitorResult = identifyVisitor(userForAnalytics);

          setUser(decoded.UserAccountID);

          const eventData = {
            user_id: decoded.UserAccountID,
            visitor_type: visitorResult.visitorType,
            login_count: visitorResult.session.loginCount,
          };

          if (visitorResult.isFirstLogin) {
            trackEvent(VISITOR_EVENTS.USER_FIRST_AUTHENTICATION, eventData);
          } else {
            trackEvent(VISITOR_EVENTS.USER_RETURNING_AUTHENTICATION, eventData);
          }

          try {
            const demographics = analyzeDemographics({
              DateOfBirth: decoded.DateOfBirth,
              FirstName: decoded.FirstName,
              EmployeeSalary: decoded.EmployeeSalary,
            });

            const anonymizedDemographics =
              getAnonymizedDemographics(demographics);

            trackEvent(VISITOR_EVENTS.USER_DEMOGRAPHICS_DETECTED, {
              user_id: decoded.UserAccountID,
              ...anonymizedDemographics,
            });
          } catch {
            // Handle demographic analysis errors silently to avoid impacting user experience
          }
        } catch {
          // Handle analytics errors silently to avoid impacting user experience
        }

        /*         if (decoded.IsDefaultPasswordChanged === "False") {
          setShowNewPasswordScreen(true);
        } else { */
        if (isEnrolledStatus(decoded.EnrolledStatus)) {
          navigate(`/?${searchParams}`); // Redirect to original destination after login
        } else {
          navigate("/wizard");
        }
        // }
      }
    } catch (error: any) {
      if (error.status === 403) {
        setError(t("auth.login.errors.invalid_credentials"));
      } else {
        setError(t("auth.login.errors.general_error"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  {
    /**Now handled by the Create Password Page, will delete laer */
  }
  /* const handleCreatePassword = async (data: createPasswordInput) => {
    setIsLoading(true);

    try {
      const trimmedPassword = data.password.trim();

      await updatePassword({
        userAccountID: parseInt(user.id),
        currentPassword: password.trim(),
        newPassword: trimmedPassword,
      });
      //dispatch(updateDefaultPasswordStatus("True"));
      navigate("/wizard");
    } catch (err) {
      setError(
        "Invalid credentials, please try again with your correct password"
      );
    } finally {
      setIsLoading(false);
    }
  }; */

  return (
    <PublicLayout>
      <div className="flex-grow flex items-center justify-center px-4  h-full font-outfit">
        <div className="w-full max-w-4xl flex justify-center">
          {/* Left side - Education program */}
          <div className="hidden order-last md:order-first w-full md:w-1/2 pr-8 md:flex flex-col justify-center">
            <h2 className="text-xl md:text-2xl mb-4">
              {t("auth.sootchy_program.title", {
                partnerName: window.partner?.title ?? "Sootchy",
              })}
            </h2>
            <ul className="space-y-2">
              <li className="flex items-center py-2">
                <svg
                  className="w-5 h-5 text-green-500 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                {t("auth.sootchy_program.benefits.auto_setup")}
              </li>
              <li className="flex items-center py-2">
                <svg
                  className="w-5 h-5 text-green-500 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                {t("auth.sootchy_program.benefits.employer_match")}
              </li>
              <li className="flex items-center py-2">
                <svg
                  className="w-5 h-5 text-green-500 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                {t("auth.sootchy_program.benefits.tax_free_growth")}
              </li>
            </ul>
          </div>

          {/* Right side - Login form */}
          <div className="font-outfit w-[420px] mt-[40px]">
            {/* {showNewPasswordScreen ? (
              <CreateNewPasswordForm
                loading={isLoading}
                error={error}
                onSubmit={handleCreatePassword}
                resetError={() => setError("")}
              />
            ) : ( */}
            <LoginForm
              error={error}
              onSubmit={handleLogin}
              loading={isLoading}
              username={username}
              password={password}
              resetError={() => setError("")}
            />
            {/* )} */}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default Login;
